import { useEffect, useState } from 'react'
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material'
import { useHost, type Reference, type ReferenceOption } from '../host'
import { tokens } from '../theme'

/**
 * The host's own references (a contract, a lot) as form fields: picked from
 * the host's list when it gives a search, typed otherwise. The value is the
 * host's id, stored and returned as given; nothing here interprets it.
 */
export function ReferenceFields({
  values, onChange, disabled, dense,
}: {
  values: Record<string, string>
  onChange: (key: string, value: string | null) => void
  disabled?: boolean
  /** Label beside the field, as the plot screen's attributes are laid out. */
  dense?: boolean
}) {
  const { references } = useHost()
  if (!references?.length) return null
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: dense ? 2 : 3 }}>
      {references.map((ref) => (
        <Box
          key={ref.key}
          sx={dense ? { display: 'grid', gridTemplateColumns: '96px 1fr', alignItems: 'center', gap: 2 } : {}}
        >
          <Typography
            component="label"
            sx={{ font: `500 13px ${tokens.font.sans}`, display: 'block', mb: dense ? 0 : 1.5, color: dense ? 'text.secondary' : 'text.primary' }}
          >
            {ref.label}{ref.required && !dense ? ' *' : ''}
          </Typography>
          <ReferenceField reference={ref} value={values[ref.key] ?? ''} disabled={disabled} onChange={(v) => onChange(ref.key, v)} />
        </Box>
      ))}
    </Box>
  )
}

/** References the host marked required that have no value yet. */
export function useMissingReferences(values: Record<string, string>): string[] {
  const { references } = useHost()
  return (references ?? []).filter((r) => r.required && !values[r.key]).map((r) => r.label)
}

function ReferenceField({
  reference, value, onChange, disabled,
}: { reference: Reference; value: string; onChange: (value: string | null) => void; disabled?: boolean }) {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])

  if (!reference.search) {
    return (
      <TextField
        size="small"
        fullWidth
        value={draft}
        disabled={disabled}
        inputProps={{ 'aria-label': reference.label, maxLength: 200 }}
        onChange={(e) => setDraft(e.target.value)}
        // Saved on leaving the field, not on every key.
        onBlur={() => draft.trim() !== value && onChange(draft.trim() || null)}
      />
    )
  }
  return <SearchedReference reference={reference} value={value} onChange={onChange} disabled={disabled} />
}

function SearchedReference({
  reference, value, onChange, disabled,
}: { reference: Reference; value: string; onChange: (value: string | null) => void; disabled?: boolean }) {
  const [input, setInput] = useState('')
  const [options, setOptions] = useState<ReferenceOption[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Asks the host as the user types; a later answer replaces an earlier one.
  useEffect(() => {
    let live = true
    setLoading(true)
    setError(null)
    const timer = setTimeout(() => {
      Promise.resolve(reference.search!(input))
        .then((found) => live && setOptions(found))
        .catch((err: Error) => live && setError(err.message || 'The list could not be loaded.'))
        .finally(() => live && setLoading(false))
    }, 250)
    return () => { live = false; clearTimeout(timer) }
  }, [input, reference])

  // The stored value is the host's id; its label is shown when the host's
  // list has it, the id itself otherwise.
  const selected = value ? options.find((o) => o.value === value) ?? { value, label: value } : null

  return (
    <Autocomplete
      size="small"
      fullWidth
      disabled={disabled}
      options={options}
      value={selected}
      loading={loading}
      filterOptions={(o) => o}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      getOptionLabel={(o) => o.label}
      onInputChange={(_e, text, reason) => reason === 'input' && setInput(text)}
      onChange={(_e, option) => onChange(option?.value ?? null)}
      noOptionsText={error ?? (input ? 'No matches' : 'Type to search')}
      renderInput={(params) => (
        <TextField
          {...params}
          inputProps={{ ...params.inputProps, 'aria-label': reference.label }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={14} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}
